import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import swal from "sweetalert2";
import { SubscriptionLike } from "rxjs";
import { Cliente } from "src/app/shared/model/ajustesUsuario/cliente.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { GenericoService } from "src/app/shared/services/generico.service";
import { GestionclientesService } from "../gestionclientes.service";

type ClienteListState = {
  currentPage?: number;
  pageSize?: number;
  searchTerm?: string;
};

@Component({
  selector: "app-gestion-clientes",
  standalone: true,
  templateUrl: "./gestion-clientes.component.html",
  styleUrls: ["./gestion-clientes.component.css"],
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    NgFor,
    NgClass,
    NgIf,
  ],
})
export class GestionClientesComponent implements OnInit, OnDestroy {
  clientes = new Cliente();
  listaClientes: Cliente[] = [];
  shed = new ConsultaList<Cliente>();
  totalRegistros = 0;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;
  regresaPag = false;
  searchTerm = "";
  private subscription?: SubscriptionLike;

  @HostBinding("class.is-open")
  isOpen = false;

  constructor(
    private clientService: GestionclientesService,
    private generico: GenericoService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.subscription = this.generico.isOpen.subscribe((res) => {
      this.regresaPag = res;
    });

    this.shed.bean = new Cliente();
    this.restoreListState();
    queueMicrotask(() => {
      this.consultaPagina(this.currentPage);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.generico.getEstatusPantalla(false);
  }

  agregarcliente() {
    this.router.navigate(["/CLIENTES/GESTION_CLIENTES/alta"], {
      state: { listState: this.buildListState() },
    });
  }

  update(cliente: Cliente) {
    this.router.navigate(["/CLIENTES/GESTION_CLIENTES/edicion", cliente.id], {
      state: {
        cliente: this.cloneCliente(cliente),
        listState: this.buildListState(),
      },
    });
  }

  delete(cliente: Cliente) {
    const title =
      cliente.estatus === "BAJA"
        ? "Estás a punto de activar al cliente"
        : "Estás a punto de dar de baja al cliente";

    swal
      .fire({
        title,
        text: "¿Deseas continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "No, cancelar",
      })
      .then((result) => {
        if (result.value) {
          this.clientService.deleteClient(cliente).subscribe((res) => {
            // El servidor rechaza la baja de un cliente que no es de la
            // empresa de la sesion (00002) y dice por que.
            if (res?.pCodRetorno === "00002") {
              swal.fire({
                text: res.pMensaje || "No fue posible cambiar el estatus del cliente.",
                buttonsStyling: false,
                customClass: { confirmButton: "btn btn-danger" },
                icon: "error",
              });
            }
            this.consultaPagina(this.currentPage);
          });
        }
      });
  }

  filtroBusqueda(filtro: string) {
    this.searchTerm = filtro ?? "";
    this.shed.param1 = this.searchTerm;
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.listaClientes.length === 0;
    this.cdr.detectChanges();
    this.shed.regInicial = (page - 1) * this.pageSize;
    this.shed.regXPagina = this.pageSize;

    this.clientService.filtroBusquedaClienteGestion(this.shed).subscribe({
      next: (res) => {
        this.totalRegistros = this.resolveTotalRegistros(res);
        this.listaClientes = Array.isArray(res?.list) ? res.list : [];

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaPagina(maxPages);
          return;
        }

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listaClientes = [];
        this.totalRegistros = 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  get totalPages(): number {
    return this.totalRegistros > 0
      ? Math.ceil(this.totalRegistros / this.pageSize)
      : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getStatusLabel(cliente: Cliente): string {
    return cliente?.estatus === "ACTIVO" ? "Activo" : "Baja";
  }

  getStatusClass(cliente: Cliente): string {
    return cliente?.estatus === "ACTIVO" ? "is-active" : "is-inactive";
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  /** Boton "Regresar": lo activa Administracion de proyectos
   *  (modal-agregar-proyecto, getEstatusPantalla(true)) al mandar aqui a dar de alta un cliente. */
  regresaProyectos() {
    this.generico.getEstatusPantalla(false);
    this.router.navigate(["ODP/admproyectos"]);
  }

  private resolveTotalRegistros(res: ConsultaList<Cliente>): number {
    const response = res as ConsultaList<Cliente> & Record<string, unknown>;
    const candidates = [
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
      response?.param4,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(res?.list) ? res.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }

  private restoreListState(): void {
    const listState = history.state?.listState as ClienteListState | undefined;
    if (!listState) {
      return;
    }

    const restoredPage = Number(listState.currentPage);
    if (Number.isFinite(restoredPage) && restoredPage > 0) {
      this.currentPage = restoredPage;
    }

    const restoredPageSize = Number(listState.pageSize);
    if (
      Number.isFinite(restoredPageSize) &&
      this.pageSizeOptions.includes(restoredPageSize)
    ) {
      this.pageSize = restoredPageSize;
    }

    if (typeof listState.searchTerm === "string") {
      this.searchTerm = listState.searchTerm;
      this.shed.param1 = this.searchTerm;
    }
  }

  private buildListState(): ClienteListState {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
    };
  }

  private cloneCliente(cliente: Cliente): Cliente {
    return JSON.parse(JSON.stringify(cliente)) as Cliente;
  }
}
